// A product's Recordings page (design 5.3: "grouped by module, with search"; no board draws it, so
// its words are on the list for Adam): every recording of the product in its week or module, with
// where they are in it, and a search across the titles and chapters of what has opened for them.
// A chapter found opens the recording at that minute. A mastermind's are its group's sessions,
// tagged, by month (design 5.3), in Accelerator.dc.html's Recordings (TaggedRecordings.jsx).
// Once any of the product's recordings has Adam's own thumbnail (decision 47, 12 Oct), every row
// starts with its 16:9 frame: the thumbnail, or the grey date panel (product/recordingThumbs.js);
// until then the rows are as they were.
import { useState } from 'react'
import * as f from '../lib/format.js'
import { Link } from '../lib/router.jsx'
import { chapterTime, linkStamp } from '../lib/timestamp.js'
import { useProductData } from './ProductData.jsx'
import { isReleased, moduleName, progressFor } from './model.js'
import { Tick } from './Icons.jsx'
import { TaggedRecordings } from './TaggedRecordings.jsx'
import { anyThumbnail, recordingThumb } from './recordingThumbs.js'
import { RecordingThumb, useRecordingThumbs } from './RecordingThumb.jsx'

// Where they are in 1 recording, in the Library's and FEHome's words
export function recordingStatus(data, r) {
  if (!isReleased(data, r.id)) return 'Opens after the live session'
  const pr = progressFor(data, r.id)
  if (pr?.watched_at) return 'Watched'
  if (pr && pr.position_seconds > 10) return `Carry on from ${f.clock(pr.position_seconds)}`
  return 'Not started'
}

export function recordingGroups(data, product, term = '') {
  const q = term.trim().toLowerCase()
  const mods = (data.modules || []).filter(m => m.is_published).sort((a, b) => a.sort_order - b.sort_order)
  const recs = (data.recordings || []).slice().sort((a, b) => a.sort_order - b.sort_order)
  const content = new Map((data.content || []).map(c => [c.recording_id, c]))
  const any = anyThumbnail(recs)
  const groups = []
  const row = r => {
    const mod = r.module_id ? mods.find(m => m.id === r.module_id) : null
    const open = isReleased(data, r.id)
    const key = mod && recs.filter(x => x.module_id === mod.id).length === 1 ? mod.key : r.key || mod?.key
    const href = open && key ? `${product.path}/${key}` : null
    const title = mod && (r.title === mod.title || r.title === mod.label) ? moduleName(mod) : (r.part ? `Part ${r.part}: ${r.title}` : r.title)
    const chapters = open && q ? (content.get(r.id)?.chapters || []).filter(c => c.title.toLowerCase().includes(q))
      .map(c => ({ ...c, href: `${href}?t=${linkStamp(c.start_seconds)}` })) : []
    const hit = !q || title.toLowerCase().includes(q) || (mod && moduleName(mod).toLowerCase().includes(q)) || chapters.length > 0
    return hit ? {
      id: r.id, title, status: recordingStatus(data, r), href, chapters, watched: Boolean(progressFor(data, r.id)?.watched_at),
      thumb: recordingThumb(data, r, any),
    } : null
  }
  for (const m of mods) {
    const rows = recs.filter(r => r.module_id === m.id).map(row).filter(Boolean)
    if (rows.length) groups.push({ key: m.id, heading: m.label, rows })
  }
  const loose = recs.filter(r => !r.module_id).map(row).filter(Boolean)
  if (loose.length) groups.push({ key: 'other', heading: null, rows: loose })
  return groups
}

export function RecordingsPage({ product }) {
  if ((product?.type) === 'mastermind') return <TaggedRecordings product={product} headingLevel="h1" months />
  return <ModuleRecordings product={product} />
}

function ModuleRecordings({ product }) {
  const { data } = useProductData()
  const [term, setTerm] = useState('')
  const groups = recordingGroups(data, data.product || product, term)
  const thumbs = useRecordingThumbs(groups.flatMap(g => g.rows.map(r => r.thumb)))
  return (
    <div className="pl">
      <section className="pl-head pl-head--row">
        <h1 className="pl-h1">Recordings</h1>
        <input className="pl-search" type="search" aria-label="Search the recordings" placeholder="Search the recordings"
          value={term} onChange={e => setTerm(e.target.value)} />
      </section>
      {!groups.length && <p className="pp-empty">{term.trim() ? 'Nothing in the recordings matches that.' : 'No recordings here yet.'}</p>}
      {groups.map(g => (
        <section key={g.key} className="pl-group">
          {g.heading && <h2 className="pl-h2">{g.heading}</h2>}
          <div className="pl-list">
            {g.rows.map(r => (
              <div key={r.id} className={r.thumb ? 'pl-row pl-row--rec pl-row--thumb' : 'pl-row pl-row--rec'}>
                <RecordingThumb card={r.thumb} thumbs={thumbs} className="pl-thumb" />
                <span className="pl-text">
                  <span className="pl-title">{r.title}</span>
                  <span className={r.watched ? 'pl-meta pl-meta--done' : 'pl-meta'}>{r.watched && <Tick />}{r.status}</span>
                  {r.chapters.map(c => (
                    <Link key={c.start_seconds} className="pl-chapter" to={c.href}>
                      <span className="pl-chapter-time">{chapterTime(c.start_seconds)}</span>{c.title}
                    </Link>
                  ))}
                </span>
                <span className="pl-actions">
                  {r.href ? <Link className="pl-btn" to={r.href}>Open</Link> : null}
                </span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
